import type { FeatureCollection } from 'geojson';

import type { EntityTableFilterParams } from '@/components/map/utils/entityMapFilters';

import { apiClient } from './apiClient';
import { normaliseFeatureCollectionResponse } from './geoJsonUtils';
import { REPEAT_ARRAY_PARAMS } from './statsApi';
import type { NetworkStats, StatsPeriod } from './statsApi';

/**
 * Network Explorer feeds. All span every client the estate exposes to the explorer, so
 * none takes a client id — the server decides the row set from the caller's permission
 * and the per-client exclusion flag, never from a request parameter. The statistics and
 * the cells feed take the explorer's committed map filters (the same serialised dimensions
 * the Find & Fix stats bar sends), so bubbles, header and markers describe one estate.
 */

/** The single-client stats shape plus the one figure only the estate-wide view has. */
export interface NetworkExplorerStats extends NetworkStats {
    /** Distinct clients (networks) that contributed at least one POI row. */
    totalNetworks: number;
}

/**
 * Estate-wide statistics for the explorer header (GET /network-explorer/stats) over the chosen
 * period, under the committed filters (null = every dimension inactive).
 */
export async function fetchNetworkExplorerStats(
    period: StatsPeriod,
    filters?: EntityTableFilterParams | null,
    signal?: AbortSignal,
): Promise<NetworkExplorerStats> {
    const response = await apiClient.get<NetworkExplorerStats>('/network-explorer/stats', {
        params: { period, ...(filters ?? {}) },
        paramsSerializer: REPEAT_ARRAY_PARAMS,
        signal,
    });
    return response.data;
}

/**
 * The estate's POI and leak activity pre-binned into grid cells (GET /network-explorer/clusters)
 * under the committed filters (null = every dimension inactive): one point per cell at the
 * mean entity position, carrying `count_total`, `count_pois`, `count_leaks` and one
 * `count_poi_<status>` / `count_leak_<status>` counter per presentation status. The browser
 * clusters these further; no individual record is ever downloaded.
 */
export async function fetchNetworkExplorerClusters(
    filters?: EntityTableFilterParams | null,
    signal?: AbortSignal,
): Promise<FeatureCollection> {
    const response = await apiClient.get<unknown>('/network-explorer/clusters', {
        params: { ...(filters ?? {}) },
        paramsSerializer: REPEAT_ARRAY_PARAMS,
        signal,
    });
    return normaliseFeatureCollectionResponse(response.data);
}

/**
 * The individual POI and leak records inside a viewport across every included client
 * (GET /network-explorer/entities/{bbox}): the single-client viewport feed's features, plus
 * each record's `client_id` and `client_name`. Drawn from the marker handover (the District
 * Meter Area band's mid stop) inward, where the cells feed hands over. The committed filters
 * are applied to these in the browser, by the same transform Find & Fix uses.
 */
export async function fetchNetworkExplorerEntitiesForBounds(bbox: string): Promise<FeatureCollection> {
    const response = await apiClient.get<unknown>(`/network-explorer/entities/${encodeURIComponent(bbox)}`);
    return normaliseFeatureCollectionResponse(response.data);
}
